import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI, profileAPI } from '../services/api';

interface User {
  id: string;
  username: string;
  companyId: string;
}

interface Company {
  id: string;
  companyName: string;
}

interface CompanyProfile {
  address: {
    street: string;
    city: string;
    state: string;
    pincode: string;
  };
  email: string;
  [key: string]: any;
}

interface AuthContextType {
  user: User | null;
  company: Company | null;
  token: string | null;
  profileComplete: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
  checkProfileComplete: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Function to check if profile is complete
const isProfileComplete = (profile: CompanyProfile | null): boolean => {
  if (!profile) return false;
  
  // Check if any required field has "To be updated" placeholder
  const hasPlaceholder = 
    profile.address?.street === 'To be updated' ||
    profile.address?.city === 'To be updated' ||
    profile.address?.state === 'To be updated' ||
    profile.address?.pincode === '000000' ||
    !profile.email ||
    profile.email.trim() === '' ||
    profile.email.endsWith('@company.com');
  
  return !hasPlaceholder;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [profileComplete, setProfileComplete] = useState(true);
  const [loading, setLoading] = useState(true);

  const checkProfileComplete = async () => {
    try {
      const response = await profileAPI.get();
      const complete = isProfileComplete(response.data);
      setProfileComplete(complete);
      return complete;
    } catch (error) {
      console.error('Error checking profile:', error);
      return false;
    }
  };

  useEffect(() => {
    // Check for stored token on mount
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      // Verify token and get user info
      authAPI.getMe()
        .then(async response => {
          setUser(response.data.user);
          setCompany(response.data.company);
          // Check if profile is complete
          await checkProfileComplete();
        })
        .catch(() => {
          // Token invalid, clear storage
          localStorage.removeItem('token');
          setToken(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const response = await authAPI.login(username, password);
      const { token: newToken, user: newUser, company: newCompany } = response.data;
      
      setToken(newToken);
      setUser(newUser);
      setCompany(newCompany);
      localStorage.setItem('token', newToken);
      
      // Check if profile is complete after login
      await checkProfileComplete();
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Login failed');
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setCompany(null);
    setProfileComplete(true);
    localStorage.removeItem('token');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        company,
        token,
        profileComplete,
        login,
        logout,
        isAuthenticated: !!token,
        loading,
        checkProfileComplete
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

